import React, { useState } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Input, Badge, message, Select } from 'antd';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import {
  HomeOutlined,
  ShoppingOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuOutlined,
  SearchOutlined,
  BellOutlined,
  HeartOutlined,
  SettingOutlined,
  GlobalOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  TagsOutlined,
  FireOutlined,
  TeamOutlined,
  ShopOutlined,
  FileTextOutlined,
  BarChartOutlined,
  UnorderedListOutlined,
  ExclamationCircleOutlined,
  MessageOutlined,
  StarOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import './Layout.css';
import { logout } from '../../redux/userSlice';

const { Header, Content, Footer } = Layout;
const { Search } = Input;

// Custom styles for compact menu
const menuStyle = {
  // lineHeight: '40px',
  borderBottom: 'none',
};

const menuItemStyle = {
  padding: '0 12px',
  margin: '0 4px',
};

const buttonStyle = {
  padding: '8px 16px',
  height: '40px',
  margin: '0 4px',
  fontSize: '14px',
};

const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => state.user);
  console.log('DEBUG currentUser:', currentUser);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const isLiveAuctionDetailPage = /^\/live-auctions\/[^/]+$/.test(location.pathname);

  const handleSearch = (value) => {
    if (value.trim()) {
      navigate(`/search-results?q=${encodeURIComponent(value.trim())}`);
    } else {
      // If search is empty, go to search results page to show all auctions
      navigate('/search-results');
    }
  };

  const [currentLanguage, setCurrentLanguage] = useState(
    () => (typeof window !== 'undefined' ? localStorage.getItem('app_language') || 'en' : 'en')
  );

  const clearTranslateCookies = () => {
    const hostname = window.location.hostname;
    const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
    const domains = [undefined];
    if (hostname && hostname !== 'localhost' && hostname !== '127.0.0.1') {
      domains.push(hostname, `.${hostname}`);
      const parts = hostname.split('.');
      if (parts.length > 2) {
        const root = parts.slice(-2).join('.');
        domains.push(root, `.${root}`);
      }
    }
    domains.forEach((domain) => {
      const domainPart = domain ? `; domain=${domain}` : '';
      document.cookie = `googtrans=; ${expire}; path=/${domainPart}`;
    });
  };

  const handleLanguageChange = (val) => {
    if (isLiveAuctionDetailPage) {
      message.warning('Translation is disabled during live auctions to keep bidding working correctly.');
      return;
    }

    localStorage.setItem('app_language', val);
    setCurrentLanguage(val);

    if (val === 'en') {
      const combo = document.querySelector('.goog-te-combo');
      if (combo) {
        combo.value = '';
        combo.dispatchEvent(new Event('change'));
      }
      clearTranslateCookies();
      window.location.reload();
      return;
    }

    const tryTranslate = (retries) => {
      const combo = document.querySelector('.goog-te-combo');
      if (combo) {
        combo.value = val;
        combo.dispatchEvent(new Event('change'));
        message.success('Translating page...');
      } else if (retries > 0) {
        setTimeout(() => tryTranslate(retries - 1), 500);
      } else {
        document.cookie = `googtrans=/en/${val}; path=/`;
        if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
          document.cookie = `googtrans=/en/${val}; path=/; domain=${window.location.hostname}`;
        }
        window.location.reload();
      }
    };
    tryTranslate(6);
  };

  const adminMenuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/admin/dashboard">Dashboard</Link>,
    },
    {
      key: 'auctions',
      icon: <ShoppingOutlined />,
      label: <Link to="/admin/auctions">Auctions</Link>,
    },
    {
      key: 'management',
      icon: <SettingOutlined />,
      label: 'Management',
      children: [
        {
          key: 'categories',
          icon: <AppstoreOutlined />,
          label: <Link to="/admin/categories">Categories</Link>,
        },
        {
          key: 'users',
          icon: <TeamOutlined />,
          label: <Link to="/admin/users">Users</Link>,
        },
        {
          key: 'companies',
          icon: <ShopOutlined />,
          label: <Link to="/admin/companies">Companies</Link>,
        },
      ],
    },
    {
      key: 'support',
      icon: <FileTextOutlined />,
      label: 'Support',
      children: [
        {
          key: 'disputes',
          icon: <ExclamationCircleOutlined />,
          label: <Link to="/admin/disputes">Disputes</Link>,
        },
        {
          key: 'feedback',
          icon: <MessageOutlined />,
          label: <Link to="/admin/feedback">Feedback</Link>,
        },
      ],
    },
    {
      key: 'analytics',
      icon: <BarChartOutlined />,
      label: 'Analytics',
      children: [
        {
          key: 'reports',
          icon: <BarChartOutlined />,
          label: <Link to="/reports">Reports</Link>,
        },
      ],
    },
  ];

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <DashboardOutlined />,
      onClick: () => navigate('/dashboard'),
    },
    {
      type: 'divider',
    },
    {
      key: 'profile',
      label: 'My Profile',
      icon: <UserOutlined />,
      onClick: () => navigate('/profile'),
    },
    // {
    //   key: 'settings',
    //   label: 'Settings',
    //   icon: <SettingOutlined />,
    //   onClick: () => navigate('/settings'),
    // },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: () => {
        dispatch(logout());
        navigate('/');
      },
    },
  ];

  const adminDropdownItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <DashboardOutlined />,
      onClick: () => navigate('/admin/dashboard'),
    },
    {
      key: 'auctions',
      label: 'Auctions',
      icon: <ShoppingOutlined />,
      onClick: () => navigate('/admin/auctions'),
    },
    {
      type: 'divider',
    },
    {
      key: 'management-group',
      label: 'Management',
      type: 'group',
    },
    {
      key: 'categories',
      label: 'Categories',
      icon: <AppstoreOutlined />,
      onClick: () => navigate('/admin/categories'),
    },
    {
      key: 'users',
      label: 'Users',
      icon: <TeamOutlined />,
      onClick: () => navigate('/admin/users'),
    },
    {
      key: 'companies',
      label: 'Companies',
      icon: <ShopOutlined />,
      onClick: () => navigate('/admin/companies'),
    },
    {
      type: 'divider',
    },
    {
      key: 'support-group',
      label: 'Support',
      type: 'group',
    },
    {
      key: 'disputes',
      label: 'Disputes',
      icon: <ExclamationCircleOutlined />,
      onClick: () => navigate('/admin/disputes'),
    },
    {
      key: 'feedback',
      label: 'Feedback',
      icon: <MessageOutlined />,
      onClick: () => navigate('/admin/feedback'),
    },
    {
      type: 'divider',
    },
    {
      key: 'analytics-group',
      label: 'Analytics',
      type: 'group',
    },
    {
      key: 'reports',
      label: 'Reports',
      icon: <BarChartOutlined />,
      onClick: () => navigate('/reports'),
    },
    {
      type: 'divider',
    },
    {
      key: 'profile',
      label: 'My Profile',
      icon: <UserOutlined />,
      onClick: () => navigate('/profile'),
    },
    // {
    //   type: 'divider',
    // },
    // {
    //   key: 'settings',
    //   label: 'Settings',
    //   icon: <SettingOutlined />,
    //   onClick: () => navigate('/settings'),
    // },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: () => {
        dispatch(logout());
        navigate('/');
      },
    },
  ];

  const favoriteMenuItems = [
    {
      key: 'favorite-auctions',
      label: 'Favorite Auctions',
      icon: <AppstoreOutlined />,
      onClick: () => navigate('/favorite-auctions'),
    },
    {
      key: 'favorite-lots',
      label: 'Favorite Lots',
      icon: <TagsOutlined />,
      onClick: () => navigate('/favorite-lots'),
    },
  ];

  const userQuickMenuItems = [
    {
      key: 'my-auctions',
      label: 'My Auctions',
      icon: <ShoppingOutlined />,
      onClick: () => navigate('/my-auctions'),
    },
    {
      key: 'my-lots',
      label: 'My Lots',
      icon: <TagsOutlined />,
      onClick: () => navigate('/my-lots'),
    },
    {
      key: 'bid-history',
      label: 'Bid History',
      icon: <FileTextOutlined />,
      onClick: () => navigate('/analytics'),
    },
  ];

  const mainMenuItems = currentUser?.roles?.includes('admin') ? adminMenuItems : [
    // {
    //   key: 'home',
    //   icon: <HomeOutlined />,
    //   label: <Link to="/">Home</Link>,
    // },
    {
      key: 'auctions',
      icon: <ShoppingOutlined />,
      label: 'Browse Auctions',
      children: [
        {
          key: 'all-auctions',
          icon: <AppstoreOutlined />,
          label: <Link to="/auctions">All Auctions</Link>,
        },
        {
          key: 'live-auctions',
          icon: <ThunderboltOutlined />,
          label: <Link to="/live-auctions">Live Auctions</Link>,
        },
        {
          key: 'featured-auctions',
          icon: <FireOutlined />,
          label: <Link to="/featured-auctions">Featured Auctions</Link>,
        },
      ],
    },
    {
      key: 'lots',
      icon: <UnorderedListOutlined />,
      label: 'Lots',
      children: [
        {
          key: 'all-lots',
          icon: <TagsOutlined />,
          label: <Link to="/browse-lots">All Lots</Link>,
        },
        {
          key: 'featured-lots',
          icon: <FireOutlined />,
          label: <Link to="/featured-lots">Featured Lots</Link>,
        },
      ],
    },
    {
      key: 'categories',
      icon: <GlobalOutlined />,
      label: <Link to="/category-results">Categories</Link>,
    },
  ];

  // Mobile menu items - with dropdown structure
  const mobileMenuItems = [
    {
      key: 'auctions-mobile',
      icon: <ShoppingOutlined />,
      label: 'Auctions',
      children: [
        {
          key: 'all-auctions-mobile',
          icon: <AppstoreOutlined />,
          label: <Link to="/auctions">All Auctions</Link>,
        },
        {
          key: 'live-auctions-mobile',
          icon: <ThunderboltOutlined />,
          label: <Link to="/live-auctions">Live Auctions</Link>,
        },
        {
          key: 'featured-auctions-mobile',
          icon: <FireOutlined />,
          label: <Link to="/featured-auctions">Featured Auctions</Link>,
        },
      ],
    },
    {
      key: 'lots-mobile',
      icon: <UnorderedListOutlined />,
      label: 'Lots',
      children: [
        {
          key: 'all-lots-mobile',
          icon: <TagsOutlined />,
          label: <Link to="/browse-lots">All Lots</Link>,
        },
        {
          key: 'featured-lots-mobile',
          icon: <FireOutlined />,
          label: <Link to="/featured-lots">Featured Lots</Link>,
        },
      ],
    },
    {
      key: 'categories-mobile',
      icon: <GlobalOutlined />,
      label: <Link to="/category-results">Categories</Link>,
    },
  ];

  return (
    <Layout className="app-layout">
      <Header className="main-header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" className="logo-link">
              <img src="/assets/images/logo.png" alt="Maveshibids" className="logo-image" width={183} height={69} />
            </Link>
            {currentUser?.roles?.includes('admin') ? (
              <Menu
                mode="horizontal"
                className="main-nav admin-nav-desktop"
                selectedKeys={(() => {
                  const path = location.pathname;
                  // Handle admin routes
                  if (path.startsWith('/admin/')) {
                    if (path === '/admin/dashboard') return ['dashboard'];
                    if (path === '/admin/auctions') return ['auctions'];
                    if (path === '/admin/categories') return ['categories'];
                    if (path === '/admin/users') return ['users'];
                    if (path === '/admin/companies') return ['companies'];
                    if (path === '/admin/disputes') return ['disputes'];
                    if (path === '/admin/feedback') return ['feedback'];
                  }
                  if (path === '/reports') return ['reports'];
                  return [];
                })()}
                items={mainMenuItems}
                style={menuStyle}
                triggerSubMenuAction="hover"
              />
            ) : (
              <Menu
                mode="horizontal"
                className="main-nav"
                selectedKeys={(() => {
                  const path = location.pathname;
                  // Handle nested menu items
                  if (path === '/auctions' || path === '/') return ['all-auctions'];
                  if (path === '/live-auctions' || path.startsWith('/live-auctions/')) return ['live-auctions'];
                  if (path === '/featured-auctions') return ['featured-auctions'];
                  if (path === '/browse-lots' || path.includes('/lots/')) return ['all-lots'];
                  if (path === '/featured-lots') return ['featured-lots'];
                  if (path === '/category-results' || path === '/categories') return ['categories'];
                  return [];
                })()}
                items={mainMenuItems}
                style={menuStyle}
                triggerSubMenuAction="hover"
              />
            )}
          </div>
          <div className="header-right">
            {/* Language switcher moved into the footer */}
            {/* {!currentUser?.roles?.includes('admin') && (
              <Search
                placeholder="Search auctions..."
                className="search-bar"
                prefix={<SearchOutlined className="search-icon" />}
                allowClear
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onSearch={handleSearch}
                enterButton
              />
            )} */}
            {currentUser ? (
              <div className="user-actions" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {!currentUser.roles?.includes('admin') && (
                  <>
                    <Dropdown
                      menu={{ items: userQuickMenuItems }}
                      placement="bottomRight"
                      trigger={['click']}
                      overlayClassName="user-quick-menu-dropdown"
                    >
                      <Button
                        type="text"
                        icon={<AppstoreOutlined />}
                        className="action-button"
                        style={buttonStyle}
                      />
                    </Dropdown>
                    <Dropdown
                      menu={{ items: favoriteMenuItems }}
                      placement="bottomRight"
                      trigger={['click']}
                      overlayClassName="favorite-dropdown-menu"
                    >
                      <Button
                        type="text"
                        icon={<HeartOutlined />}
                        className="action-button favorite-action-button"
                        size="small"
                        style={buttonStyle}
                      />
                    </Dropdown>
                  </>
                )}
                <Dropdown
                  menu={{ items: currentUser.roles?.includes('admin') ? adminDropdownItems : userMenuItems }}
                  placement="bottomRight"
                  trigger={['click']}
                  overlayClassName="user-dropdown-menu"
                >
                  <div className="user-profile" style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                    <Avatar
                      size="small"
                      icon={<UserOutlined />}
                      className="user-avatar"
                    />
                    <span className="user-name" style={{ fontSize: '14px' }}>{currentUser.name}</span>
                  </div>
                </Dropdown>
              </div>
            ) : (
              <>
                <div className="auth-buttons auth-buttons-desktop">
                  <Button type="link" onClick={() => navigate('/login')}>
                    Sign In
                  </Button>
                  <Button type="primary" onClick={() => navigate('/register')}>
                    Register
                  </Button>
                </div>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'login',
                        label: 'Sign In',
                        icon: <UserOutlined />,
                        onClick: () => navigate('/login'),
                      },
                      {
                        key: 'register',
                        label: 'Register',
                        icon: <ThunderboltOutlined />,
                        onClick: () => navigate('/register'),
                      },
                    ],
                  }}
                  placement="bottomRight"
                  trigger={['click']}
                  overlayClassName="auth-dropdown-menu"
                >
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<UserOutlined />}
                    className="auth-buttons-mobile-trigger"
                  />
                </Dropdown>
              </>
            )}
          </div>
        </div>
      </Header>
      <div className="mobile-menu-bar-wrapper">
        <div className="mobile-menu-bar">
          <Menu
            mode="horizontal"
            items={mobileMenuItems}
            className="mobile-nav-menu"
            selectedKeys={[
              location.pathname === '/auctions' || location.pathname === '/' ? 'all-auctions-mobile' :
                location.pathname === '/live-auctions' || location.pathname.startsWith('/live-auctions/') ? 'live-auctions-mobile' :
                  location.pathname === '/featured-auctions' ? 'featured-auctions-mobile' :
                    location.pathname === '/browse-lots' || location.pathname.includes('/lots/') ? 'all-lots-mobile' :
                      location.pathname === '/featured-lots' ? 'featured-lots-mobile' :
                        location.pathname === '/category-results' || location.pathname === '/categories' ? 'categories-mobile' : null
            ].filter(Boolean)}
            triggerSubMenuAction="click"
          />
        </div>
      </div>
      <div className="content-wrapper">
        <Content className="main-content">
          <div
            className={`content-container${isLiveAuctionDetailPage ? ' notranslate' : ''}`}
            translate={isLiveAuctionDetailPage ? 'no' : undefined}
          >
            <Outlet />
          </div>
        </Content>
      </div>
      <Footer className="main-footer">
        <div className="footer-simple">
          <p className="copyright">Maveshibids © {new Date().getFullYear()} All rights reserved</p>
          <div className="notranslate footer-language-wrap" translate="no">
            <Select
              value={currentLanguage}
              size="small"
              className="footer-language-selector"
              disabled={isLiveAuctionDetailPage}
              title={isLiveAuctionDetailPage ? 'Translation is disabled during live auctions' : undefined}
              onChange={handleLanguageChange}
              getPopupContainer={(trigger) => trigger.parentNode}
              options={[
                { label: '🇺🇸 English', value: 'en' },
                { label: '🇵🇰 Urdu (اردو)', value: 'ur' },
              ]}
            />
          </div>
          <div className="footer-links">
            <Link to="/privacy-policy">Privacy</Link>
            <span className="footer-divider">|</span>
            <Link to="/terms-and-conditions">Terms</Link>
          </div>
        </div>
      </Footer>

      {/* WhatsApp Floating Button — guests only */}
      {!currentUser && (
        <div className="whatsapp-float">
          <a
            href="https://wa.me/923281419656?text=Hello! I'm interested in Maveshibids auctions and would like to know more."
            className="whatsapp-button"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="whatsapp-tooltip">Contact us on WhatsApp</div>
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path fill="#fff" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
          </a>
        </div>
      )}
    </Layout>
  );
};

export default AppLayout;


